function $q(ctx, selector, callback) {
  ctx.querySelectorAll(selector).forEach((el, index) => {
    callback(el, index, ctx);
  });
}

$q.first = function first(ctx, selector, callback) {
  const el = ctx.querySelector(selector);
  if (el) callback(el, 0, ctx);
};

// Cached query selector
function $cqs(selector, context = document) {
  // Check if the selector is already cached for this context
  if (!$cqs.cache) {
    // If not, create a cache object
    $cqs.cache = new WeakMap();
  }

  // Check if the selector is already in the cache for this context
  if (!$cqs.cache.has(context)) {
    $cqs.cache.set(context, new Map());
  }
  const cache = $cqs.cache.get(context);
  if (!cache.has(selector)) {
    // If not, query the DOM for the context and store the result in the cache
    cache.set(selector, context.querySelector(selector));
  }

  // Return the cached value
  return cache.get(selector);
}

const _HYDRATE_SKIP = Symbol("SKIP");
const FN_ATTR_EFFECTS_LOOKUP = {
  "x-show": (el, result) => {
    const show = !!result;
    el.toggleAttribute("hidden", !show);
    // el.style.display = show ? '' : 'none'
  },
  "x-text": (el, result) => {
    el.textContent = result == null ? "" : result;
  },
  "x-effect": null,
};
// `x-if` is also supported. If defined, it must return a boolean.
// skip running of the rest of the effects if it returns false

const SUPPORTED_KEYS = Object.keys(FN_ATTR_EFFECTS_LOOKUP);

const _children_cache = new WeakMap();
export const hydrateFromHtml = ({
  container,
  preferCachedChildren = true,
  // tuple of params and args
  context,
}) => {
  const fnAttrsLookup = FN_ATTR_EFFECTS_LOOKUP;
  const data = context.reduce(
    (acc, tuple) => {
      acc.params.push(tuple[0]);
      acc.args.push(tuple[1]);
      return acc;
    },
    { params: [], args: [] }
  );

  let els = _children_cache.get(container);
  if (!els || !preferCachedChildren) {
    els = [container].concat(
      Array.from(
        container.querySelectorAll(
          `${SUPPORTED_KEYS.map((x) => `[${x}]`).join(", ")}`
        )
      )
    );
    _children_cache.set(container, els);
  }

  els.forEach((el) => {
    const condFnString = el.getAttribute("x-if");
    if (condFnString) {
      const condFnCacheKey = `__fn_x-if`;
      let condFn = el[condFnCacheKey];
      if (condFnString && !condFn) {
        condFn = new Function(
          "$el",
          "$SKIP",
          "$cqs",
          ...data.params,
          `return (${condFnString})`
        );
        el[condFnCacheKey] = condFn;
      }

      if (condFn) {
        const shouldContinue = condFn.call(container, el, _HYDRATE_SKIP, $cqs, ...data.args);
        if (!shouldContinue) return;
      }
    }

    for (const fnAttr in fnAttrsLookup) {
      if (!Object.prototype.hasOwnProperty.call(fnAttrsLookup, fnAttr)) {
        continue;
      }
      const condAttr = `${fnAttr}-if`;

      const ourEffect = fnAttrsLookup[fnAttr];
      const userFnString = el.getAttribute(fnAttr);
      const condFnString = el.getAttribute(condAttr);

      if (!userFnString) {
        continue;
      }

      const fnCacheKey = `__fn_${fnAttr}`;
      const condFnCacheKey = `__fn_${condAttr}`;

      let userFn = el[fnCacheKey];

      if (!userFn) {
        // if there is our effect, then we expect the `userFn` to be returnable
        const returnable = !!ourEffect;
        const fnbody = returnable ? `return (${userFnString})` : userFnString;
        userFn = new Function("$el", "$SKIP", "$cqs", ...data.params, fnbody);
        el[fnCacheKey] = userFn;
      }

      let condFn = el[condFnCacheKey];
      if (condFnString && !condFn) {
        condFn = new Function(
          "$el",
          "$SKIP",
          "$cqs",
          ...data.params,
          `return (${condFnString})`
        );
        el[condFnCacheKey] = condFn;
      }

      try {
        if (condFn) {
          const shouldContinue = condFn(container, _HYDRATE_SKIP, $cqs, ...data.args);
          if (shouldContinue === false) continue;
        }

        // Run user fn
        const result = userFn.call(container, el, _HYDRATE_SKIP, $cqs, ...data.args);
        if (result === _HYDRATE_SKIP) continue;

        // Run effect - if any
        if (ourEffect) {
          ourEffect(el, result);
        }
      } catch (e) {
        if (!el.hasAttribute("x-no-err")) {
          console.error("[hydrateFromHtml] :: ", e.message);
        }
      }
    }
  });
};

// ---------------------------------------------------------------
//
function tick(ms) {
  if (ms) {
    return new Promise((f) => setTimeout(f, ms));
  }

  return new Promise((f) => requestAnimationFrame(f));
}

/**
 * Creates a custom element with core functionality
 * @template {HTMLElement} T
 * 
 * @param {string} tag - The tag name of the custom element.
 * @param {T} HTMLElement - The base class of the custom element.
 * @param {(EnhancedCustomElement:  { new (): T; prototype: T; }, tag: string) => T} callback - The callback function used to extend the base class.
 * @param {object} defineOpts - The options for defining the custom element.
 * @return {T} The defined custom element class.
 */
function createCustomElement(tag, HTMLElement, callback, defineOpts) {
  const existing = customElements.get(tag);
  if (existing) return existing;

  class BaseClass extends HTMLElement {
    constructor() {
      super();
    }

    async connectedCallback() {
      this._disconnecting = false;
      await tick(50);
      if (this._disconnecting) return;

      this._ac = new AbortController();
      this.mount && this.mount();
    }

    async disconnectedCallback() {
      this._disconnecting = true;
      await tick(50);
      if (!this._disconnecting) return;
      this.unmount && this.unmount();
      this._ac?.abort();
      this._ac = null;
    }
  }

  const klass = callback(BaseClass, tag);
  customElements.define(tag, klass, defineOpts);
  return klass;
}

createCustomElement("event-reactor", HTMLElement, (Base) => {
  return class EventReactor extends Base {
    mount() {
      this._listenFrom = this.getAttribute("listen-from");
      if (!this._listenFrom) return;
      this._listenFrom = document.getElementById(this._listenFrom);

      if (!this._listenFrom) return;

      this._hasStaticChildren = !this.hasAttribute("dynamic-childs");

      this._events = this.getAttribute("event")
        ?.split(",")
        .map((raw) => {
          const [name, mappedTo = name] = raw.split("->");
          return { name, mappedTo: mappedTo.replace(/\:/g, "_") };
        });

      if (!Array.isArray(this._events) || this._events.length === 0) return;

      if (this._events.length === 0) return;

      this._stateContext = this._events.map((ev, index) => [ev.mappedTo, null]);

      this._events.forEach((ev, index) => {
        this._listenFrom.addEventListener(
          ev.name,
          (domEvent) => {
            if (!this.isConnected) return;

            this._stateContext[index][1] = domEvent.detail;

            hydrateFromHtml({
              container: this,
              preferCachedChildren: !this._hasStaticChildren,
              context: [
                ["formatMoney", formatMoney],
                ["$event", domEvent],
                ["$detail", domEvent.detail],
                ...this._stateContext,
              ],
            });
          },
          { signal: this._ac.signal }
        );
      });

      this.removeAttribute("x-dehydrated");
    }

    unmount() {
      this._stateContext = null;
    }
  };
});

createCustomElement("x-inventory-by-location", HTMLElement, (Base, elementTag) => {
  class InventoryByLocation extends Base {
    constructor() {
      super();
      this.productId = this.getAttribute("product-id");
      this.variantId = this.getAttribute("variant-id");
      this.locationId = this.getAttribute("location-id");

      let endpoint = this.getAttribute("endpoint")
      if (!endpoint) throw new Error(`Missing attribute "endpoint" on element "${elementTag}"`)

      this.endpointUrl = new URL(endpoint);
      this.endpointUrl.searchParams.set('product_id', this.productId) 
    }

    mount() {
      // alert("mounted");
      this.updateVariantInventoryCount();
    }

    unmount() {
      // alert("unmounted");
    }

    async updateVariantInventoryCount() {
      const data = await this.fetchInventory();
      const result = InventoryByLocation.getInventory(
        data,
        this.productId,
        this.variantId,
        this.locationId
      );

      hydrateFromHtml({
        container: this,
        preferCachedChildren: !this.hasAttribute("dynamic-childs"),
        context: [["data", data]],
      });
    }

    async fetchInventory() {
      if (!this._inventory) {
        this._inventory = await InventoryByLocation.fetch(this.endpointUrl.toString())
      }
      return this._inventory;
    }
  }

  InventoryByLocation.cache = new Map();
  InventoryByLocation.fetch = async function fetcher(endpoint) {
    const cached = InventoryByLocation.cache.get(endpoint);
    if (cached) return cached;
    const promise = fetch(endpoint).then((r) => r.json());
    InventoryByLocation.cache.set(endpoint, promise);
    return promise;
  };

  InventoryByLocation.getInventory = function getInventory(
    apiData,
    pid,
    vid,
    lid
  ) {
    const data = apiData[pid].variants[vid];
    return { total: data.total, count: data.at_location[lid] || 0 };
  };

  return InventoryByLocation;
});
